"use client";

import { ChatScenario } from "@ui/components/core/ChatScenario";
import type { CmsChatScenario } from "@ui/components/core/ChatScenario/cms";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { resolveSpeechEnginePreviewScenario } from "./resolveSpeechEnginePreviewScenario";

const DEFAULT_REPLAY_PAUSE_MS = 2000;

interface SpeechEngineHelpChatDemoProps {
  /** Scripted preview from CMS; falls back to the default El support scenario when missing or incomplete. */
  chatScenario?: CmsChatScenario | null;
  /** Pause before replay when `loop` is true (default 2000 ms). */
  replayPauseMs?: number | null;
  /** Delay between messages in the scripted preview (`ChatScenario`; default 350 ms). */
  additionalMessageDelayMs?: number | null;
  /** Delay between parts inside an assistant message (`ChatScenario`; default 300 ms). */
  additionalPartDelayMs?: number | null;
  /**
   * Number of scripted messages to render instantly (no animation) on mount. The animated reveal starts from the next
   * message. Defaults to 0 (animate from the first message).
   */
  initialVisibleMessageCount?: number | null;
  /**
   * Shows typing ellipsis bubbles (user- or assistant-styled) during delays before each next message and between
   * assistant message parts (`ChatScenario`; default true).
   */
  typingIndicatorBetweenMessages?: boolean;
  /** Fades out messages as they scroll off the top of the visible area (`ChatScenario`; default true). */
  topFade?: boolean;
  /** Repeat the scripted scenario after it finishes. */
  loop?: boolean;
  /** How many scripted messages are currently mounted (for voice handoff). */
  onVisibleMessageCountChange?: (visibleCount: number) => void;
  /** How many scripted messages are fully revealed, excluding in-bubble typing (for voice handoff). */
  onSettledMessageCountChange?: (settledCount: number) => void;
}

export function SpeechEngineHelpChatDemo({
  chatScenario,
  replayPauseMs,
  additionalMessageDelayMs,
  additionalPartDelayMs,
  initialVisibleMessageCount,
  typingIndicatorBetweenMessages = true,
  topFade = true,
  loop = false,
  onVisibleMessageCountChange,
  onSettledMessageCountChange,
}: SpeechEngineHelpChatDemoProps = {}) {
  const [playCycle, setPlayCycle] = useState(0);
  const replayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolvedReplayPauseMs =
    typeof replayPauseMs === "number" && Number.isFinite(replayPauseMs) && replayPauseMs >= 0
      ? replayPauseMs
      : DEFAULT_REPLAY_PAUSE_MS;

  const resolvedMessageDelayMs =
    typeof additionalMessageDelayMs === "number" &&
    Number.isFinite(additionalMessageDelayMs) &&
    additionalMessageDelayMs >= 0
      ? additionalMessageDelayMs
      : undefined;

  const resolvedPartDelayMs =
    typeof additionalPartDelayMs === "number" && Number.isFinite(additionalPartDelayMs) && additionalPartDelayMs >= 0
      ? additionalPartDelayMs
      : undefined;

  const resolvedInitialVisibleMessageCount =
    typeof initialVisibleMessageCount === "number" &&
    Number.isFinite(initialVisibleMessageCount) &&
    initialVisibleMessageCount > 0
      ? Math.floor(initialVisibleMessageCount)
      : undefined;

  const baseScenario = useMemo(() => resolveSpeechEnginePreviewScenario(chatScenario), [chatScenario]);

  // When an explicit message delay is provided (e.g. for Storybook), override each message's
  // hardcoded loadingBeforeMs so the caller-controlled pacing actually takes effect. Without
  // this, additionalMessageDelayMS is only a fallback and is ignored whenever loadingBeforeMs
  // is set on the message.
  const scenario = useMemo(() => {
    if (resolvedMessageDelayMs === undefined) return baseScenario;
    const applyDelay = (messages: typeof baseScenario.messages) =>
      messages.map(m => ({ ...m, loadingBeforeMs: resolvedMessageDelayMs }));
    return {
      ...baseScenario,
      initialMessages: baseScenario.initialMessages ? applyDelay(baseScenario.initialMessages) : undefined,
      messages: applyDelay(baseScenario.messages),
    };
  }, [baseScenario, resolvedMessageDelayMs]);

  const scheduleReplay = useCallback(() => {
    if (!loop) {
      return;
    }
    if (replayTimeoutRef.current !== null) {
      clearTimeout(replayTimeoutRef.current);
    }
    replayTimeoutRef.current = setTimeout(() => {
      replayTimeoutRef.current = null;
      setPlayCycle(n => n + 1);
    }, resolvedReplayPauseMs);
  }, [loop, resolvedReplayPauseMs]);

  useEffect(() => {
    if (!loop && replayTimeoutRef.current !== null) {
      clearTimeout(replayTimeoutRef.current);
      replayTimeoutRef.current = null;
    }
    return () => {
      if (replayTimeoutRef.current !== null) {
        clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
    };
  }, [loop]);

  return (
    <div className='tw-h-full tw-w-full tw-min-h-0 tw-max-w-sm tw-pb-11 tw-pt-8 tw-opacity-60'>
      <ChatScenario
        key={playCycle}
        active
        anchor='center'
        scenario={scenario}
        additionalMessageDelayMS={resolvedMessageDelayMs}
        additionalPartDelayMS={resolvedPartDelayMs}
        initialVisibleMessageCount={resolvedInitialVisibleMessageCount}
        typingIndicatorBetweenMessages={typingIndicatorBetweenMessages}
        typingMode='same-bubble'
        topFade={topFade}
        assistantMessageVariants={{ variant: "glass" }}
        userMessageVariants={{ variant: "glass" }}
        splitConfig={{ bottomCount: 3, gapPx: 32 }}
        onVisibleMessageCountChange={onVisibleMessageCountChange}
        onSettledMessageCountChange={onSettledMessageCountChange}
        onComplete={loop ? scheduleReplay : undefined}
      />
    </div>
  );
}
